import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./scss/inventory.scss";
import {
  INVENTORY,
  ADD_STOCK,
  TRANSACTIONS,
} from "../../constants/route-paths";
import {
  getInventoryItems,
  getTransactionsList,
  listCategories,
  getInventoryCounts,
} from "../../actions/inventory.action";
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import CustomTable from '../../components/table/table';
import Popover from 'react-bootstrap/Popover';
import Cookies from "js-cookie";
import { format } from "date-fns";
import { useNavigate } from "react-router";

const InventoryList = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const catalogs = useSelector((state) => state.catalog.catalogs);
  const { writePermission } = props;

  const [items, setItems] = useState([]);
  const [itemId, setItemId] = useState(null);
  const [filter, setFilter] = useState("total");
  const [transactions, setTransactions] = useState([]);
  const [limit, setLimit] = useState(25);
  const [offset, setOffset] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [counts, setCounts] = useState({
    totalItems: 0,
    warehouse: 0,
    site: 0
  });
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const isLoadingRef = useRef(false);
  const lastCallParamsRef = useRef(null);
  const lastCallTimeRef = useRef(0);
  const ignoreNextPageChange = useRef(false);

  const columns = [
    { title: "S No", field: "sno" },
    { title: "Item Description", field: "item" },
    { title: "Catalog", field: "categoryName" },
    { title: "Category", field: "manufacturer" },
    { title: "Part No.", field: "partNo" },
    { title: "Reserve Stock", field: "reservedStock" },
    { title: "Internal Stock", field: "deStock" },
  ];

  const transactionColumns = [
    { title: "S No", field: "sno" },
    {
      title: "PO No.",
      field: "poId",
      render: (rowData) => {
        if (rowData.poId === "NO_PO" || rowData.poId === "No_PO") return "NO PO";
        if (rowData.poId === "INTERNAL STOCK" || rowData.poId === "DE_STOCK") return "INTERNAL STOCK";
        // Display PO Number from PurchaseOrder relation if available
        if (rowData.purchaseOrder) {
          return rowData.purchaseOrder.f_id || rowData.purchaseOrder.id || rowData.poId;
        }
        return rowData.poId;
      }
    },
    { title: "Transaction Type", field: "transactionType" },
    { title: "Transaction Location", field: "location" },
    { title: "Item Name", field: "itemName" },
    { title: "Quantity", field: "quantity" },
    { title: "Allocated To", field: "allocateToName" },
    { title: "Time", field: "createdAtTime" },
    { title: "Date", field: "createdAt" },
  ];

  const getItems = useCallback(() => {
    const params = {
      limit,
      offset,
      search,
      categoryFilter,
    };

    setIsLoading(true);
    setError(null);

    return dispatch(getInventoryItems(params))
      .then((response) => {
        const itemsData = response.items || [];
        const totalCountData = response.totalCount || 0;
        const data = [];
        itemsData &&
          itemsData.forEach((item, index) => {
            const categoryName = (item.category && item.category.name) || "";
            data.push({
              sno: offset + index + 1,
              categoryName,
              ...item,
            });
          });
        setItems(data);
        setTotalCount(totalCountData);
        setIsLoading(false);
      })
      .catch((error) => {
        setError(error.message || 'Failed to load inventory items');
        setIsLoading(false);
        setItems([]);
        setTotalCount(0);
      });
  }, [limit, offset, search, categoryFilter, dispatch]);

  const getTransactions = useCallback(() => {
    const params = {
      limit,
      offset,
      search,
      location: filter === 'site' ? 'SITE' : 'WAREHOUSE'
    };

    setIsLoading(true);
    setError(null);

    return dispatch(getTransactionsList(params))
      .then((response) => {
        const transactionsList = response.transactions || [];
        const totalCountData = response.totalCount || 0;
        const transactionsData = [];
        transactionsList.forEach((transaction, index) => {
          const itemName = transaction.items && transaction.items.item;
          transactionsData.push({
            sno: offset + index + 1,
            itemName,
            ...transaction,
            createdAtTime: format(new Date(transaction.createdAt), "hh:mm a"),
            createdAt: format(new Date(transaction.createdAt), "dd/MM/yyyy"),
          });
        });
        setTransactions(transactionsData);
        setTotalCount(totalCountData);
        setIsLoading(false);
      })
      .catch((error) => {
        setError(error.message || 'Failed to load transactions');
        setIsLoading(false);
        setTransactions([]);
        setTotalCount(0);
      });
  }, [dispatch, limit, offset, search, filter]);

  const fetchData = useCallback(() => {
    if (filter === 'warehouse' || filter === 'site') {
      return getTransactions();
    } else {
      return getItems();
    }
  }, [filter, getTransactions, getItems]);

  const fetchDataWithDebounce = useCallback(() => {
    const currentParams = {
      filter,
      offset,
      limit,
      search,
      categoryFilter,
    };

    const now = Date.now();

    // Prevent duplicate calls if already loading
    if (isLoadingRef.current) {
      return;
    }

    // Debounce: prevent calls within 100ms
    if (now - lastCallTimeRef.current < 100) {
      return;
    }

    // Prevent duplicate calls with same parameters
    if (
      lastCallParamsRef.current &&
      JSON.stringify(lastCallParamsRef.current) === JSON.stringify(currentParams)
    ) {
      return;
    }

    isLoadingRef.current = true;
    lastCallParamsRef.current = currentParams;
    lastCallTimeRef.current = now;

    fetchData()
      .finally(() => {
        isLoadingRef.current = false;
      });
  }, [filter, offset, limit, search, categoryFilter, fetchData]);

  // Initial Mount
  useEffect(() => {
    const recordsPerPage = Cookies.get("recordsPerPage")
      ? parseInt(Cookies.get("recordsPerPage"))
      : 25;
    setLimit(recordsPerPage);

    dispatch(listCategories());

    // Initial fetch is handled by the dependency effect below because state is initialized
    // However, we need to ensure it runs once correctly.
    // The original code called fetchDataWithDebounce inside setState callback of limit.
    // Here, setting limit will trigger the effect below.
  }, [dispatch]);

  // Effect to handle data fetching on state changes (replaces componentDidUpdate)
  useEffect(() => {
    fetchDataWithDebounce();
  }, [fetchDataWithDebounce]);

  // Effect to fetch inventory counts when filters change
  useEffect(() => {
    const timer = setTimeout(() => {
      const params = {
        search,
        categoryFilter
      };
      dispatch(getInventoryCounts(params)).then(countsData => {
        if (countsData) {
          setCounts(countsData);
        }
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [dispatch, search, categoryFilter, filter]);


  const handleMenu = (id) => {
    setItemId(id);
  };

  const handlePageChange = (page, newLimit) => {
    if (ignoreNextPageChange.current) {
      ignoreNextPageChange.current = false;
      return;
    }
    const newOffset = (page - 1) * newLimit;
    // Only update if page actually changed
    if (currentPage === page && limit === newLimit) {
      return;
    }
    setCurrentPage(page);
    setOffset(newOffset);
    setLimit(newLimit);
  };

  const handleLimitChange = (newLimit) => {
    // Only update if limit actually changed
    if (limit === newLimit) {
      return;
    }
    ignoreNextPageChange.current = true;
    Cookies.set("recordsPerPage", newLimit);
    setLimit(newLimit);
    setOffset(0);
    setCurrentPage(1);
  };

  const searchHandler = (value) => {
    setSearch(value);
    setOffset(0);
  };

  const changeFilter = (selectedCategories) => {
    const categoryIds = selectedCategories.join(",");
    setCategoryFilter(categoryIds);
    setOffset(0);
    setCurrentPage(1);
  };

  const addStock = () => {
    navigate(`/${INVENTORY}/${ADD_STOCK}`);
  };

  const navigateTransactions = () => {
    navigate(`/${INVENTORY}/${TRANSACTIONS}`);
  };

  const allocateItem = () => {
    navigate(`/${INVENTORY}/allocate-item/${itemId}`);
  };

  const viewStock = () => {
    navigate(`/${INVENTORY}/po-stock/${itemId}`);
  };

  const overAllStock = () => {
    navigate(`/${INVENTORY}/overall-stock/${itemId}`);
  };

  const filterItems = (type) => {
    // Prevent redundant API call if already on the same tab
    if (filter === type) {
      return;
    }

    setFilter(type);
    setOffset(0);
    setCurrentPage(1);
    setTotalCount(0);
    setItems([]);
    setTransactions([]);
    setSearch("");
    setCategoryFilter("");
  };



  // Safe check for counts is handled by initial state

  // Show loading state
  if (isLoading && items.length === 0 && transactions.length === 0) {
    return (
      <div className="purchase-order-block invetory-list-page">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading inventory data...</span>
            </div>
            <p className="mt-3 text-muted">Loading inventory data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error && items.length === 0 && transactions.length === 0) {
    return (
      <div className="purchase-order-block invetory-list-page">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="alert alert-danger" role="alert">
              <h4 className="alert-heading">Error Loading Inventory</h4>
              <p>{error}</p>
              <hr />
              <button 
                className="btn btn-outline-primary"
                onClick={() => {
                  setError(null);
                  fetchDataWithDebounce();
                }}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="purchase-order-block invetory-list-page">
      <div className="row">
        <div className="w-auto">
          <h2 className="content-heading text-uppercase">Inventory</h2>
        </div>

        {writePermission && (<div className="w-auto ms-auto mt-auto">
          <button onClick={navigateTransactions} className="secondarybtn secondarybtn-outline btn-ch ms-auto btn btn-primary">Inventory Transactions</button>
          {/* <button onClick={createRFQ} className="secondarybtn secondarybtn-outline btn-ch ms-auto btn btn-primary">Create RFQ</button> */}
          <button onClick={addStock} className="secondarybtn secondarybtn-outline btn-ch ms-2 btn btn-primary">Add Stock</button>
        </div>)}
      </div>

      <div className="row">
        <div className="col-12">
          <Tabs
            defaultActiveKey="total"
            id="uncontrolled-tab-example"
            className="mb-3"
            onSelect={(k) => {
              switch (k) {
                case 'total': filterItems('total'); break;
                case 'warehouse': filterItems('warehouse'); break;
                case 'site': filterItems('site'); break;
                default: break;
              }
            }}
          >
            <Tab eventKey="total" title={`Total Items (${counts.totalItems})`} tabClassName="total-tab">
              <CustomTable
                columns={columns}
                data={items}
                isAction={writePermission}
                handleMenu={(data) => handleMenu(data.id)}
                menu={(data) => (
                  <Popover id="popover-basic">
                    <Popover.Body>
                      <ul className="action-block-list">
                        <li onClick={allocateItem}>Allocate Item</li>
                        <li onClick={viewStock}>View PO</li>
                        <li onClick={overAllStock}>View Stock Location</li>
                      </ul>
                    </Popover.Body>
                  </Popover>
                )}
                fileName='Total Items List'
                itemsPerPage={limit}
                limit={limit}
                currentPage={currentPage}
                paginationCount={totalCount}
                pageHandler={handlePageChange}
                limitChangeHandler={handleLimitChange}
                isLoading={isLoading}
                searchHandler={searchHandler}
                externalSearchValue={search}
                categoryFilterProps={{
                  categories: (catalogs || []).map((c) => ({
                    value: String(c.id),
                    templateNameWithCount: c.name,
                  })),
                  selectedCategories: (categoryFilter ? categoryFilter.split(",") : []),
                  onCategoryChange: changeFilter,
                  label: "Catalog Filter",
                  categoryFilterClass: "material-category-filter",
                }}
              />
            </Tab>
            <Tab eventKey="warehouse" title={`Ware House (${counts.warehouse})`} tabClassName="warehouse-tab">
              <CustomTable
                columns={transactionColumns}
                data={transactions}
                isAction={false}
                fileName='Warehouse Items List'
                itemsPerPage={limit}
                limit={limit}
                currentPage={currentPage}
                paginationCount={totalCount}
                pageHandler={handlePageChange}
                limitChangeHandler={handleLimitChange}
                isLoading={isLoading}
                searchHandler={searchHandler}
                externalSearchValue={search}
              />
            </Tab>
            <Tab eventKey="site" title={`Site (${counts.site})`} tabClassName="site-tab">
              <CustomTable
                columns={transactionColumns}
                data={transactions}
                isAction={false}
                fileName='Site Items List'
                itemsPerPage={limit}
                limit={limit}
                currentPage={currentPage}
                paginationCount={totalCount}
                pageHandler={handlePageChange}
                limitChangeHandler={handleLimitChange}
                isLoading={isLoading}
                searchHandler={searchHandler}
                externalSearchValue={search}
              />
            </Tab>

          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default InventoryList;
